import React, { useState } from 'react';
import { Box, Chip, Stack, Typography, useMediaQuery } from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { TableDisplay } from '../miscellaneous-components/table-display';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import gaiaTheme from '../../../root-components/theme';

export type RecalculationJobStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface IRecalculationHistoryRow {
  id: string;
  createdAt: string;
  /** What the job changed, e.g. "UK Government DEFRA Conversion Factors to 2024". */
  change: string;
  status: RecalculationJobStatus;
  /** Outcome in words for a completed job, e.g. "13 records changed". */
  outcome?: string | null;
  errorMessage?: string | null;
  beforeTotals: { totalLocation?: number } | null;
  afterTotals: { totalLocation?: number } | null;
}

interface RecalculationHistoryTableProps {
  title: string;
  changeLabel: string;
  rows: IRecalculationHistoryRow[];
}

const STATUS_CHIPS: Record<RecalculationJobStatus, { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
  COMPLETED: { label: 'Completed', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' },
  IN_PROGRESS: { label: 'In progress', color: 'warning' },
  PENDING: { label: 'Queued', color: 'default' },
};

// Jobs that ran before totals were snapshotted, and jobs that did not complete, hold none.
const readTotal = (totals: { totalLocation?: number } | null): number | null => {
  if (!totals || totals.totalLocation == null) return null;
  const value = Number(totals.totalLocation);
  return Number.isFinite(value) ? value : null;
};

const formatTonnes = (value: number | null): string =>
  value == null ? '-' : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Shared by the factor year and exchange rate histories on the period's Conversion Factors
// tab, so the two read as one kind of record.
export const RecalculationHistoryTable: React.FC<RecalculationHistoryTableProps> = ({ title, changeLabel, rows }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  if (rows.length === 0) return null;

  const headCells: IHeadCell[] = [
    { id: 'date', label: 'Date', sortable: true, numeric: false, sortValue: 'createdAtSort', isHeading: true, includeInMobile: true },
    { id: 'change', label: changeLabel, sortable: false, numeric: false, isSubHeading: true, includeInMobile: true },
    { id: 'statusChip', label: 'Status', sortable: true, numeric: false, sortValue: 'status', includeInMobile: true },
    { id: 'before', label: 'Total before (tCO2e)', sortable: false, numeric: true, align: 'right', includeInMobile: true },
    { id: 'after', label: 'Total after (tCO2e)', sortable: false, numeric: true, align: 'right', includeInMobile: true },
    { id: 'difference', label: 'Change (tCO2e)', sortable: false, numeric: true, align: 'right', includeInMobile: true },
    { id: 'details', label: 'Details', sortable: false, numeric: false, includeInMobile: true, oneRow: true },
  ];

  const tableRows = rows.map((row) => {
    const before = readTotal(row.beforeTotals);
    const after = readTotal(row.afterTotals);
    const completed = row.status === 'COMPLETED';
    const difference = completed && before != null && after != null ? after - before : null;
    const rounded = difference == null ? null : Math.round(difference * 100) / 100;
    const chip = STATUS_CHIPS[row.status] ?? { label: row.status, color: 'default' as const };
    const failed = row.status === 'FAILED';
    const details = failed ? row.errorMessage : [row.outcome, row.errorMessage].filter(Boolean).join('. ');

    return {
      id: row.id,
      createdAtSort: new Date(row.createdAt).getTime(),
      status: row.status,
      date: new Date(row.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
      change: row.change,
      statusChip: <Chip size='small' variant='outlined' label={chip.label} color={chip.color} />,
      before: <Box component='span' sx={{ fontVariantNumeric: 'tabular-nums' }}>{completed ? formatTonnes(before) : '-'}</Box>,
      after: <Box component='span' sx={{ fontVariantNumeric: 'tabular-nums' }}>{completed ? formatTonnes(after) : '-'}</Box>,
      difference: rounded == null ? '-' : (
        <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={0.5}>
          {rounded > 0 && <ArrowUpward fontSize='inherit' sx={{ color: 'error.main' }} />}
          {rounded < 0 && <ArrowDownward fontSize='inherit' sx={{ color: 'success.main' }} />}
          <Box component='span' sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
            {rounded === 0 ? 'No change' : formatTonnes(Math.abs(rounded))}
          </Box>
        </Stack>
      ),
      details: details
        ? <Typography variant='body2' sx={{ color: failed ? 'error.main' : 'text.secondary', maxWidth: 420, whiteSpace: 'normal' }}>{details}</Typography>
        : '-',
    };
  });

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant='subtitle1' sx={{ color: 'text.primary', mb: 1 }}>{title}</Typography>
      <TableDisplay
        headCells={headCells}
        rows={tableRows}
        selected={selected}
        setSelected={setSelected}
        perPage={5}
        defaultOrderBy='createdAtSort'
        defaultOrder='desc'
        isMobileMode={isMobileMode}
      />
    </Box>
  );
};
